import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat.js";

dayjs.extend(customParseFormat);

export type HistoryValue =
  | string
  | number
  | boolean
  | null
  | HistoryValue[]
  | { [key: string]: HistoryValue };
export type HistoryChange = [HistoryValue, HistoryValue] | HistoryValue;

// MaterialOrderStatusHandler emits these fields, plus date and status changes.
export const historyFieldLabels: Record<string, string> = {
  quantity: "Menge",
  day: "Datum",
  project: "Baustelle",
  deleted: "Storniert",
  planningStatus: "Planungsstatus",
  logistics: "Logistik",
  customer: "Kunde",
  auNumber: "AU-Nr.",
  position: "AU-Nr.",
  plant: "Werk",
  number: "Kostenstelle / Auftragsnummer",
  contact: "Ansprechpartner",
  start: "Von",
  end: "Bis",
  article: "Artikel",
  amount: "Menge",
  takt: "Takt",
  firstUnload: "Erste Entladung",
  transportInformation: "Logistik",
  easyTarget: "Entladestelle",
  transportStatus: "Trans-Status",
  critical: "Kritisch",
  plantStatus: "Werk-Status",
  plantComment: "Werkskommentar",
  firstLoad: "LKW an Waage",
  firstLoadTrans: "LKW an Waage · Transplan",
  firstUnloadTrans: "LKW an Baustelle · Transplan",
  date: "Datum",
  showAmountInTransplan: "Menge im Transplan anzeigen",
  comment: "Kommentar",
  roundsUnit: "Mengeneinheit",
  duration: "LKW-Einsatzdauer",
};

export const historyFieldLabel = (key: string): string =>
  historyFieldLabels[key] ?? key;

export const xBFormatter = (
  value: string,
  type: "WERK" | "BS" | "INFO" = "BS",
  withSuffix: boolean = false,
): string => {
  try {
    const [werkszeit, loadingStr = "0", routeStr = "0", direction = "0"] = value
      .substring(2)
      .split("+");
    if (type === "WERK") {
      return werkszeit + (withSuffix ? " Uhr" : "");
    }

    const mult = (parseInt(direction) || 0) === 1 ? -1 : 1;

    const loadingTime = parseInt(loadingStr) || 0;
    const routeTime = parseInt(routeStr) || 0;
    const baustellenzeit = dayjs(werkszeit, "HH:mm")
      .add(mult * loadingTime, "minute")
      .add(mult * routeTime, "minute")
      .format("HH:mm");

    if (type === "BS") {
      return baustellenzeit + (withSuffix ? " Uhr" : "");
    }

    return (
      "Bel. " +
      (mult === 1 ? "" : "ca. ") +
      (mult === 1 ? werkszeit : baustellenzeit) +
      (withSuffix ? " Uhr" : "") +
      ", Entl. " +
      (mult === -1 ? "" : "ca. ") +
      (mult === -1 ? werkszeit : baustellenzeit) +
      (withSuffix ? " Uhr" : "")
    );
  } catch {
    return "???";
  }
};

export function formatHistoryValue(key: string, value: HistoryValue): string {
  if (key === "showAmountInTransplan" && value === null) return "Automatisch";
  if (
    key === "takt" &&
    (value === null || value === "" || value === 0 || value === "0")
  )
    return "gleichzeitig";
  if (key === "firstUnload" && (value === null || value === ""))
    return "anschließend";
  if (key === "article" && value === null) return "Kein Artikel";
  if (value === null || value === "") return "Nicht angegeben";

  if (typeof value === "boolean") {
    if (key === "critical") return value ? "Kritisch" : "Nicht kritisch";
    if (key === "roundsUnit") return value ? "Touren" : "Artikeleinheit";
    return value ? "Ja" : "Nein";
  }

  // Preserve unfamiliar payloads as text instead of passing objects to React.
  if (typeof value === "object") return JSON.stringify(value);
  const text = String(value);
  switch (key) {
    case "takt":
      return text + " min";
    case "firstUnload":
      return !text.startsWith("x")
        ? text + " Uhr"
        : text.startsWith("xB")
          ? xBFormatter(text, "INFO", true)
          : text === "xAM"
            ? "vormittags"
            : text === "xPM"
              ? "nachmittags"
              : text;
    case "easyTarget":
      return text === "SITE"
        ? "Baustelle"
        : text === "LAGER"
          ? "Lagerplatz"
          : text;
    case "duration":
      return (
        (
          {
            FULL: "Ganztägig",
            HALF: "Halbtags",
            ROUND: "Eine Tour",
            TOUR: "Eine Tour",
            INVALID: "Nicht angegeben",
          } as Record<string, string>
        )[text] ?? text
      );
    case "date": {
      const date = dayjs(text, "YYYY-MM-DD");
      return date.isValid() ? date.format("ddd, DD.MM.YYYY") : text;
    }
    default:
      return text;
  }
}

export function formatHistoryArticle(value: HistoryValue): string {
  if (value === null) return "Kein Artikel";
  if (
    typeof value !== "object" ||
    Array.isArray(value) ||
    typeof value.name !== "string"
  ) {
    return formatHistoryValue("article", value);
  }
  return [
    value.name + (value.number == null ? "" : ` (${value.number})`),
    value.plantName,
    value.unit == null ? "" : `Einheit: ${value.unit}`,
  ]
    .filter(Boolean)
    .join(" - ");
}

// Proxy events use old/new; the original BOB timeline uses [before, after].
export function normalizeHistoryChanges(
  changes: Record<string, unknown>,
): Record<string, HistoryChange> {
  return Object.fromEntries(
    Object.entries(changes).map(([key, change]) => [
      key,
      change &&
      typeof change === "object" &&
      !Array.isArray(change) &&
      ("old" in change || "new" in change)
        ? [
            (change as Record<string, unknown>).old ?? null,
            (change as Record<string, unknown>).new ?? null,
          ]
        : (change ?? null),
    ]),
  ) as Record<string, HistoryChange>;
}
